/*
 * From https://www.redblobgames.com/maps/terrain-from-noise/
 * Copyright 2015 Red Blob Games <redblobgames@gmail.com>
 * License: Apache-2.0 <http://www.apache.org/licenses/LICENSE-2.0.html>
 */


import { createNoise2D, createNoise3D, createNoise4D } from "simplex-noise";
import PRNG from "./prng";
import Map2D from "./map2d";

export const SEED = 12345;

export function SimplexNoise2D(seed: number) { return createNoise2D(PRNG(seed)); }
export function SimplexNoise3D(seed: number) { return createNoise3D(PRNG(seed)); }
export function SimplexNoise4D(seed: number) { return createNoise4D(PRNG(seed)); }

/** Make a new map with noise, and cache previously generated maps */
let mapCache = new Map<string, Map2D>();
export function makeNoise(width: number, height: number, frequency: number, seed: number): Map2D {
    const cacheKey = `${seed}:${width}:${height}:${frequency}`;
    if (!mapCache.has(cacheKey)) {
        let map = new Map2D(width, height);
        fillNoise(map, frequency, seed);
        mapCache.set(cacheKey, map);
    }
    return mapCache.get(cacheKey);
}


/** Fill a map with one frequency of noise values from 0.0 to 1.0 */
export function fillNoise(output: Map2D, frequency: number, seed: number): void {
    const noise = SimplexNoise2D(seed);
    const aspect = output.w / output.h;
    for (let y = 0; y < output.h; y++) {
        for (let x = 0; x < output.w; x++) {
            let nx = x/output.w - 0.5, ny = y/output.h - 0.5;
            let z = noise(nx * frequency * aspect, ny * frequency);
            output.set(x, y, z / 2 + 0.5);
        }
    }
}


/** Add multiple noise maps together */
export function addMaps(output: Map2D, maps: Map2D[], amplitudes: number[]): void {
    const N = maps.length;
    if (N !== amplitudes.length) {
        throw `map count (${N}) and amplitude count (${amplitudes.length}) must be the same`;
    }

    for (let y = 0; y < output.h; y++) {
        for (let x = 0; x < output.w; x++) {
            let z = 0;
            for (let i = 0; i < N; i++) {
                z += amplitudes[i] * maps[i].get(x, y);
            }
            output.set(x, y, z);
        }
    }
}


/** Mix together several frequencies of simplex noise */
export function mixNoise(output: Map2D, spectrum: number[], frequency: number, seed=SEED): void {
    let maps = [];
    let scale = 0.0;
    let amplitudes = [];
    for (let octave = 0, exponent = 1; octave < spectrum.length; octave++, exponent *= 2) {
        scale += spectrum[octave];
        maps.push(makeNoise(output.w, output.h, frequency * exponent, seed + octave));
        amplitudes.push(spectrum[octave]);
    }
    addMaps(output, maps, amplitudes.map(function(a) { return a/scale; }));
}


/** Reshape noise by a power function */
export function reshapeNoiseExponent(output: Map2D, input: Map2D, exponent: number): void {
    const size = input.size;
    for (let id = 0; id < size; id++) {
        // The 1.21 is a fudge factor I needed to make scale better; see
        // https://digitalfreepen.com/2017/06/20/range-perlin-noise.html
        // for justification: the range of noise is sqrt(num_dimensions/4)
        output.data[id] = Math.pow(1.21*input.data[id], exponent);
    }
}
